/**
 * The picture each build cue is cut to (owner 2026-09-24: the tower building sound "doesn't match the animation").
 * Seconds from the start of the cue's sample. The samples are rendered from `art/audio/v6/design.py` TIMELINE, which
 * writes the same numbers to `art/audio/v6/build_timeline.json`; `buildCues.test.ts` holds this table to that file, to
 * TowerFeel and to the `build` clip's keys in the tower GLBs, so the sound and the animation cannot drift apart.
 *
 * `towerPlace` is cut to a placed tower's own clock, which starts on the tap: the model drops the last half metre in
 * over TowerFeel.DropSeconds while its 1.63 s `build` clip (art/hq_anim.py build_clip, 30 fps, the same for every
 * tier-1 tower) raises it — a short air rush, the landing thud with the dust ring, the scaffold rattling up, the
 * storey set, the roof dropped on, the pizzicato "built" as the lamps flash on, the scaffold packed away.
 * `towerUpgrade` / `towerUpgradeMax` are cut to the ascension (views/towers.ts): the old tower gathers for
 * TowerFeel.AscendSquash, then swaps under a flash with the brass stab.
 */
export const BuildCueBeats = {
  towerPlace: {
    /** The drop ends: the landing squash, dust ring and camera kick. */
    land: 0.12,
    /** The scaffold's top (the clip's frame 9), with a wooden knock. */
    scaffoldTop: 0.3,
    /** The storey reaches its rest from below (frame 12.8), set with a stone knock. */
    storey: 0.4267,
    /** The roof, dropped from 90 cm, hits (frame 27.4). */
    roof: 0.9133,
    /** Lamps, crystals and flames flash on (frame 34): the pizzicato chord. */
    lamps: 1.1333,
  },
  towerUpgrade: {
    /** The gathered tower swaps under the flash, beam and shockwave. */
    swap: 0.3,
  },
} as const;

/** The end of the air rush a late `towerPlace` keeps, so its thud does not start cold (s). */
const RushKeptSeconds = 0.02;

/**
 * How far a late start may skip into a cue to stay on its picture (s): the part of the sample before its first beat.
 * A cue held for WebKit's resume (mixPolicy PlayerAnswerGraceSeconds) starts that much in rather than late, so the
 * tower's thud still lands with the tower; past it the cue plays late whole rather than lose its landing.
 */
export const BuildCueLead: Partial<Record<string, number>> = {
  towerPlace: BuildCueBeats.towerPlace.land - RushKeptSeconds,
};
